import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type AppRole = "parent" | "student" | "school_admin" | "merchant" | "superadmin";

type Profile = { id: string; full_name: string | null; role: AppRole | null; school_id: string | null };

type AuthContextValue = {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  isSuperadmin: boolean;
  tenantId: string | null;
  tenantSlug: string | null;
};

const AuthContext = createContext<AuthContextValue>({
  user: null,
  profile: null,
  session: null,
  loading: true,
  signOut: async () => {},
  isSuperadmin: false,
  tenantId: null,
  tenantSlug: null,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setLoading(false);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session?.user) {
      setProfile(null);
      return;
    }
    let active = true;

    // Fetch profile and role in parallel
    Promise.all([
      supabase.from("profiles").select("id, full_name, school_id").eq("id", session.user.id).maybeSingle(),
      supabase.from("user_roles").select("role, school_id").eq("user_id", session.user.id).limit(1).maybeSingle(),
    ]).then(([profileRes, roleRes]) => {
      if (!active) return;
      if (profileRes.data) {
        setProfile({
          id: profileRes.data.id,
          full_name: profileRes.data.full_name,
          role: (roleRes.data?.role as AppRole) ?? null,
          school_id: roleRes.data?.school_id ?? profileRes.data.school_id ?? null,
        });
      }
    });

    return () => {
      active = false;
    };
  }, [session?.user?.id]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
  };

  const isSuperadmin = profile?.role === "superadmin";
  const tenantId = profile?.school_id ?? null;
  const tenantSlug = profile?.school_id ?? null;

  return (
    <AuthContext.Provider
      value={{ user: session?.user ?? null, profile, session, loading, signOut, isSuperadmin, tenantId, tenantSlug }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
